/**
 * Permission System Constants
 * Centralized definition of screens, actions, and roles
 */

// Screen identifiers
export const SCREENS = {
  COMPANIES: 'COMPANIES',
  USERS: 'USERS',
  GROUPS: 'GROUPS',
  TEAMS: 'TEAMS',
  TEAM_MEMBERS: 'TEAM_MEMBERS',
  SETTINGS: 'SETTINGS',
  LEAVE_REQUESTS: 'LEAVE_REQUESTS',
  FIRM_MANAGEMENTS: 'FIRM_MANAGEMENTS',
  CONTRACTS: 'CONTRACTS',
  OFFERS: 'OFFERS',
  JOB_MANAGEMENT: 'JOB_MANAGEMENT',
  TASKS: 'TASKS',
  JOB_ASSIGNMENTS: 'JOB_ASSIGNMENTS',
  JOB_DISTRIBUTIONS: 'JOB_DISTRIBUTIONS',
  INVOICE_MANAGEMENT: 'INVOICE_MANAGEMENT',
  REPORT_MANAGEMENT: 'REPORT_MANAGEMENT',
  PRIM_MANAGEMENT: 'PRIM_MANAGEMENT',
};

// Action types
export const ACTIONS = {
  CREATE: 'can_create',
  VIEW: 'can_view',
  EDIT: 'can_edit',
  DELETE: 'can_delete',
  DOWNLOAD: 'can_download',
};

// User roles
export const USER_ROLES = {
  ROOT: 'ROOT',
  DIRECTOR: 'DIRECTOR',
  GROUP_MANAGER: 'GROUP_MANAGER',
  TEAM_MANAGER: 'TEAM_MANAGER',
  TEAM_MEMBER: 'TEAM_MEMBER',
};

// Role configurations with UI metadata
export const ROLE_CONFIG = {
  [USER_ROLES.ROOT]: {
    label: 'Yönetici',
    color: 'error',
    protected: true,
    description: 'Sistem yöneticisi - Tüm izinler',
    level: 0,
  },
  [USER_ROLES.DIRECTOR]: {
    label: 'Direktör',
    color: 'primary',
    protected: false,
    description: 'Üst düzey yönetici - Geniş yetkiler',
    level: 1,
  },
  [USER_ROLES.GROUP_MANAGER]: {
    label: 'Grup Yöneticisi',
    color: 'secondary',
    protected: false,
    description: 'Grup düzeyinde yönetici',
    level: 2,
  },
  [USER_ROLES.TEAM_MANAGER]: {
    label: 'Takım Yöneticisi',
    color: 'info',
    protected: false,
    description: 'Takım düzeyinde yönetici',
    level: 3,
  },
  [USER_ROLES.TEAM_MEMBER]: {
    label: 'Takım Üyesi',
    color: 'default',
    protected: false,
    description: 'Standart kullanıcı',
    level: 4,
  },
};

// Screen configurations with UI metadata
export const SCREEN_CONFIG = {
  [SCREENS.DASHBOARD]: {
    name: 'Kontrol Paneli',
    icon: 'Dashboard',
    path: '/',
    requiresPermission: false,
  },
  [SCREENS.COMPANIES]: {
    name: 'Şirketler',
    icon: 'Business',
    path: '/companies',
    requiresPermission: true,
  },
  [SCREENS.USERS]: {
    name: 'Kullanıcılar',
    icon: 'People',
    path: '/users',
    requiresPermission: true,
  },
  [SCREENS.GROUPS]: {
    name: 'Gruplar',
    icon: 'Groups',
    path: '/groups',
    requiresPermission: true,
  },
  [SCREENS.TEAMS]: {
    name: 'Ekipler',
    icon: 'Groups',
    path: '/teams',
    requiresPermission: true,
  },
  [SCREENS.TEAM_MEMBERS]: {
    name: 'Ekip Üyeleri',
    icon: 'Group',
    path: '/teams',
    requiresPermission: true,
  },
  [SCREENS.SETTINGS]: {
    name: 'Ayarlar',
    icon: 'Settings',
    path: '/settings',
    requiresPermission: true,
  },
  [SCREENS.LEAVE_REQUESTS]: {
    name: 'İzin Talepleri',
    icon: 'CalendarToday',
    path: '/leave-requests',
    requiresPermission: true,
  },
  [SCREENS.FIRM_MANAGEMENTS]: {
    name: 'Firma Yönetimi',
    icon: 'Business',
    path: '/firm-management',
    requiresPermission: true,
  },
  [SCREENS.CONTRACTS]: {
    name: 'Sözleşmeler',
    icon: 'Description',
    path: '/firm-management/contracts',
    requiresPermission: true,
  },
  [SCREENS.OFFERS]: {
    name: 'Teklifler',
    icon: 'Description',
    path: '/firm-management/offers',
    requiresPermission: true,
  },
  [SCREENS.JOB_MANAGEMENT]: {
    name: 'İş Yönetimi',
    icon: 'Work',
    path: '/job-management',
    requiresPermission: true,
  },
  [SCREENS.TASKS]: {
    name: 'Görevler',
    icon: 'Assignment',
    path: '/tasks',
    requiresPermission: true,
  },
  [SCREENS.JOBS]: {
    name: 'İşler',
    icon: 'Assignment',
    path: '/jobs',
    requiresPermission: true,
  },
  [SCREENS.JOB_DISTRIBUTIONS]: {
    name: 'İş Dağılımları',
    icon: 'BarChart',
    path: '/is-dagilimlari',
    requiresPermission: true,
  },
  [SCREENS.INVOICE_MANAGEMENT]: {
    name: 'Fatura Yönetimi',
    icon: 'Receipt',
    path: '/invoice-management',
    requiresPermission: true,
  },
  [SCREENS.INVOICES]: {
    name: 'Faturalar',
    icon: 'Receipt',
    path: '/invoice-management/invoices',
    requiresPermission: true,
  },
  [SCREENS.REPORT_MANAGEMENT]: {
    name: 'Rapor Yönetimi',
    icon: 'Assessment',
    path: '/report-management',
    requiresPermission: true,
  },
  [SCREENS.PRIM_MANAGEMENT]: {
    name: 'Prim Yönetimi',
    icon: 'Percent',
    path: '/prim-management',
    requiresPermission: true,
  },
};

// Permission configurations with UI metadata
export const PERMISSION_CONFIG = {
  [ACTIONS.CREATE]: {
    name: 'Oluşturma',
    color: 'success',
    icon: 'Add',
  },
  [ACTIONS.VIEW]: {
    name: 'Okuma',
    color: 'info',
    icon: 'Visibility',
  },
  [ACTIONS.EDIT]: {
    name: 'Güncelleme',
    color: 'warning',
    icon: 'Edit',
  },
  [ACTIONS.DELETE]: {
    name: 'Silme',
    color: 'error',
    icon: 'Delete',
  },
  [ACTIONS.DOWNLOAD]: {
    name: 'Rapor İndirme',
    color: 'secondary',
    icon: 'Download',
  },
};

// Helper functions
export const getScreensArray = () => Object.values(SCREENS);
export const getActionsArray = () => Object.values(ACTIONS);
export const getRolesArray = () => Object.values(USER_ROLES);

export const isProtectedRole = role => ROLE_CONFIG[role]?.protected || false;
export const getRoleLevel = role => ROLE_CONFIG[role]?.level || 999;

export const ROLE_HIERARCHY = {
  [USER_ROLES.ROOT]: [
    USER_ROLES.ROOT,
    USER_ROLES.DIRECTOR,
    USER_ROLES.GROUP_MANAGER,
    USER_ROLES.TEAM_MANAGER,
    USER_ROLES.TEAM_MEMBER,
  ],
  [USER_ROLES.DIRECTOR]: [
    USER_ROLES.DIRECTOR,
    USER_ROLES.GROUP_MANAGER,
    USER_ROLES.TEAM_MANAGER,
    USER_ROLES.TEAM_MEMBER,
  ],
  [USER_ROLES.GROUP_MANAGER]: [
    USER_ROLES.GROUP_MANAGER,
    USER_ROLES.TEAM_MANAGER,
    USER_ROLES.TEAM_MEMBER,
  ],
  [USER_ROLES.TEAM_MANAGER]: [USER_ROLES.TEAM_MANAGER, USER_ROLES.TEAM_MEMBER],
  [USER_ROLES.TEAM_MEMBER]: [USER_ROLES.TEAM_MEMBER],
};

export const canManageUser = (currentUserRole, targetUserRole) => {
  const allowedRoles = ROLE_HIERARCHY[currentUserRole] || [];
  return allowedRoles.includes(targetUserRole);
};

export const getScreenConfig = screen => SCREEN_CONFIG[screen] || {};
export const getPermissionConfig = action => PERMISSION_CONFIG[action] || {};
export const getRoleConfig = role => ROLE_CONFIG[role] || {};
